import React, { useState } from 'react';
import PropTypes from 'prop-types';

import useDialogs from './useDialogs';
import usePlaylists from './usePlaylists';

const PlaylistContext = React.createContext();

function PlaylistProvider({
  debug,
  domain,
  handleHistory,
  hasSubscription,
  hasVideocationCatalogAccess,
  isSubscriptionAdmin,
  locale,
  managerId,
  organization,
  playlistId,
  previewBaseUrl,
  remindAllEventHandler,
  restClient,
  seeActivityHandler,
  staticImagesHostname,
  useGraphQLMutation,
  useLazyGraphQLQuery,
  userId,
  ...props
}) {
  // Global states
  const [playlistError, setPlaylistError] = useState(false);
  const [managerSelected, setManagerSelected] = useState(managerId || userId);

  const { id: organizationId } = organization || {};
  const isGlobalOrganization = organizationId === 'global';

  const canManagePlaylists = !userId || managerSelected === userId;

  const canSortPlaylists =
    isSubscriptionAdmin &&
    managerSelected === 'all' &&
    hasSubscription &&
    !isGlobalOrganization;

  const handleSelectManager = (managerId) => {
    setManagerSelected(managerId);
    if (managerId !== userId) {
      handleHistory(`manager=${managerId}`);
    } else {
      handleHistory('');
    }
  };

  const playlistContext = usePlaylists({
    handleHistory,
    isGlobalOrganization,
    locale,
    managerSelected,
    organizationId,
    setPlaylistError,
    useLazyGraphQLQuery,
    useGraphQLMutation,
  });

  const { listOwnedPlaylists, selectedPlaylist, selectPlaylistHandler } =
    playlistContext;

  const dialogsContext = useDialogs({
    useGraphQLMutation,
    domain,
    handleHistory,
    isGlobalOrganization,
    listOwnedPlaylists,
    locale,
    managerSelected,
    organizationId,
    remindAllEventHandler,
    selectedPlaylist,
    selectPlaylistHandler,
  });

  const contextValue = {
    // shared variables
    canManagePlaylists,
    canSortPlaylists,
    debug,
    domain,
    handleHistory,
    handleSelectManager,
    hasSubscription,
    hasVideocationCatalogAccess,
    isGlobalOrganization,
    isSubscriptionAdmin,
    locale,
    managerId,
    managerSelected,
    organization,
    playlistId,
    playlistError,
    previewBaseUrl,
    userId,
    // context uses
    playlists: playlistContext,
    dialogs: dialogsContext,
    // fetching methods
    seeActivityHandler,
    staticImagesHostname,
    // mutations
    restClient,
    useGraphQLMutation,
    useLazyGraphQLQuery,
  };

  return <PlaylistContext.Provider value={contextValue} {...props} />;
}

function usePlaylist() {
  const context = React.useContext(PlaylistContext);
  if (!context) {
    throw new Error('usePlaylist must be used within a PlaylistProvider');
  }

  return context;
}

PlaylistProvider.propTypes = {
  debug: PropTypes.bool,
  domain: PropTypes.oneOf(['no', 'es']).isRequired,
  handleHistory: PropTypes.func.isRequired,
  hasSubscription: PropTypes.bool.isRequired,
  hasVideocationCatalogAccess: PropTypes.bool,
  isSubscriptionAdmin: PropTypes.bool.isRequired,
  locale: PropTypes.oneOf(['no', 'en', 'es']),
  managerId: PropTypes.string,
  playlistId: PropTypes.string,
  previewBaseUrl: PropTypes.string,
  organization: PropTypes.shape({
    id: PropTypes.string,
  }),
  remindAllEventHandler: PropTypes.func,
  restClient: PropTypes.func.isRequired,
  seeActivityHandler: PropTypes.func.isRequired,
  staticImagesHostname: PropTypes.string,
  useGraphQLMutation: PropTypes.func.isRequired,
  useLazyGraphQLQuery: PropTypes.func.isRequired,
  userId: PropTypes.string,
};

PlaylistProvider.defaultProps = {
  debug: false,
};

export { PlaylistProvider, usePlaylist };
